import React, { useEffect, useState, useCallback } from "react";
import { Download, Share, Plus } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// Standalone / already-installed detection (Chromium + iOS Safari).
function isAppInstalled() {
    if (typeof window === "undefined") return false;
    const mq = window.matchMedia?.("(display-mode: standalone)")?.matches;
    const ios = window.navigator.standalone === true;
    return Boolean(mq || ios);
}

// True only for real iOS Safari (also Chrome/Firefox on iOS since they wrap
// WebKit and support "Add to Home Screen" via the share sheet).
function isIos() {
    if (typeof window === "undefined") return false;
    const ua = window.navigator.userAgent || "";
    return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

/**
 * Install App button with strict visibility rules:
 *   - Chrome/Edge/Android → clicking triggers the native install prompt via
 *     the captured `beforeinstallprompt` event.
 *   - iOS Safari (or any iOS browser) → opens an in-app dialog with the
 *     Share → Add to Home Screen instructions.
 *   - Every other browser (desktop Safari, Firefox, unsupported) → the button
 *     renders nothing at all. No fallback, no alert.
 *   - Once installed (display-mode: standalone) → renders nothing.
 */
export default function InstallAppButton({ className = "" }) {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [installed, setInstalled] = useState(() => isAppInstalled());
    const [iosModalOpen, setIosModalOpen] = useState(false);
    const ios = isIos();

    useEffect(() => {
        const onBeforeInstall = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };
        const onInstalled = () => {
            setInstalled(true);
            setDeferredPrompt(null);
        };
        window.addEventListener("beforeinstallprompt", onBeforeInstall);
        window.addEventListener("appinstalled", onInstalled);

        const mq = window.matchMedia?.("(display-mode: standalone)");
        const onModeChange = (e) => { if (e.matches) setInstalled(true); };
        mq?.addEventListener?.("change", onModeChange);

        return () => {
            window.removeEventListener("beforeinstallprompt", onBeforeInstall);
            window.removeEventListener("appinstalled", onInstalled);
            mq?.removeEventListener?.("change", onModeChange);
        };
    }, []);

    const handleClick = useCallback(async () => {
        if (deferredPrompt) {
            try {
                deferredPrompt.prompt();
                const choice = await deferredPrompt.userChoice;
                if (choice?.outcome === "accepted") setInstalled(true);
            } catch (_) { /* dismissed */ }
            setDeferredPrompt(null);
            return;
        }
        if (ios) {
            setIosModalOpen(true);
        }
    }, [deferredPrompt, ios]);

    // Hide when installed, or when the current browser has no install path.
    if (installed) return null;
    const canPrompt = Boolean(deferredPrompt);
    if (!canPrompt && !ios) return null;

    return (
        <>
            <Button
                type="button"
                variant="outline"
                onClick={handleClick}
                data-testid="install-app-btn"
                className={`rounded-xl border-sand-300 gap-1.5 ${className}`}
                title="Install BlueLedger as an app"
            >
                <Download className="h-4 w-4 text-secondary" />
                Install app
            </Button>

            <Dialog open={iosModalOpen} onOpenChange={setIosModalOpen}>
                <DialogContent className="sm:max-w-md" data-testid="install-app-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl text-ocean-700">
                            Install BlueLedger
                        </DialogTitle>
                        <DialogDescription>
                            Add BlueLedger to your Home Screen for a full-screen, app-like experience.
                        </DialogDescription>
                    </DialogHeader>

                    <ol className="space-y-3 text-sm text-ocean-700">
                        <li className="flex items-start gap-3">
                            <span className="h-6 w-6 shrink-0 rounded-full bg-ocean-50 text-ocean-700 grid place-items-center text-xs font-semibold">1</span>
                            <span className="flex-1 flex items-center gap-1.5 flex-wrap">
                                Tap the <Share className="h-4 w-4 text-secondary inline" /> <b>Share</b> icon in your browser toolbar.
                            </span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="h-6 w-6 shrink-0 rounded-full bg-ocean-50 text-ocean-700 grid place-items-center text-xs font-semibold">2</span>
                            <span className="flex-1 flex items-center gap-1.5 flex-wrap">
                                Scroll down and choose <Plus className="h-4 w-4 text-secondary inline" /> <b>Add to Home Screen</b>.
                            </span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="h-6 w-6 shrink-0 rounded-full bg-ocean-50 text-ocean-700 grid place-items-center text-xs font-semibold">3</span>
                            <span className="flex-1">Tap <b>Add</b> — BlueLedger will appear on your Home Screen with its own icon.</span>
                        </li>
                    </ol>

                    <DialogFooter>
                        <Button
                            onClick={() => setIosModalOpen(false)}
                            className="bg-ocean-700 hover:bg-ocean-700/90 text-white rounded-xl"
                            data-testid="install-app-dialog-close"
                        >
                            Got it
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
